import { type TouchpointStatusDictionaryItem } from "hooks/api/dictionary";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import type { UserAttributes } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";

import { TouchpointStatuses } from "types";
import { Permission } from "types/auth";

//admin functionality
export const canApproveSendFeedbackOnTouchpoint = (user: UserAttributes, statusName: string) => {
  return (
    hasPermission(user, Permission.PERM_CLIENT_APPROVAL) &&
    statusName === TouchpointStatuses.CLIENT_REVIEW
  );
};

export const getUserCanPublish = (user: UserAttributes) => {
  return hasPermission(user, Permission.PERM_TOUCHPOINT_PUBLISH);
};

export const isVersionEditable = ({
  status,
  versionStatusName,
}: TouchpointStatusDictionaryItem) => {
  return (
    status &&
    status !== TouchpointStatuses.CLIENT_REVIEW &&
    status !== TouchpointStatuses.ARCHIVED_CLIENT_REVIEW &&
    (versionStatusName === "Draft" ||
      versionStatusName === "Rejected draft" ||
      versionStatusName === "System draft")
  );
};

export const getIsApproved = (clientReviewStatusName: string) => {
  return clientReviewStatusName === TouchpointStatuses.APPROVED;
};

export const canCreateTouchpointFromTemplate = (status?: string): boolean =>
  status === TouchpointStatuses.APPROVED || status === TouchpointStatuses.PUBLISHED;

export const getSuccessMessage = (status: string) => {
  switch (status) {
    case TouchpointStatuses.CLIENT_REVIEW:
      return "Creative version sent to client review";
    case TouchpointStatuses.PUBLISHED:
      return "Creative version published";
    case TouchpointStatuses.APPROVED:
      return "Approval sent";
    case TouchpointStatuses.REJECTED:
      return "Feedback sent";
    default:
      return "Action applied";
  }
};

export const getFailMessage = (status: string) => {
  switch (status) {
    case TouchpointStatuses.CLIENT_REVIEW:
      return "Failed to send creative to client review";
    case TouchpointStatuses.PUBLISHED:
      return "Failed to publish creative version";
    case TouchpointStatuses.APPROVED:
      return "Failed to approve, please contact your Account Manager";
    case TouchpointStatuses.REJECTED:
      return "Failed to send feedback, please contact your Account Manager";
    default:
      return "Something went wrong";
  }
};

/** Numeric version compare for sorting and “newer than” checks. */
export const compareTouchpointVersions = (a: number, b: number): number => a - b;

/**
 * Client is viewing a feedback-received (REJECTED) version that is the **top** (highest version
 * label) in the list. Older REJECTED rows do not get the badge once a newer version exists.
 */
export const getShouldShowClientUpdatesInProgressBadge = (
  versions: TouchpointVersionAttributes[],
  selected: TouchpointVersionAttributes | undefined,
): boolean => {
  if (!selected?.version || selected.status !== TouchpointStatuses.REJECTED || !versions.length) {
    return false;
  }
  const top = versions.reduce(
    (a, b) => (compareTouchpointVersions(b.version ?? 0, a.version ?? 0) > 0 ? b : a),
    versions[0],
  );
  return top.id === selected.id;
};

function removeFromString(inputString: string, ...valuesToRemove: string[]): string {
  // Escape special regex characters in the values to remove
  const escapedValues = valuesToRemove.map((value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  // Join the values into a single regex pattern
  const regexPattern = `(${escapedValues.join("|")})`;

  // Create regex with global flag to replace all occurrences
  const regex = new RegExp(regexPattern, "g");

  // Remove the occurrences of the values
  return inputString.replace(regex, "");
}

export function removeUserIdFromString(inputString: string): string {
  return removeFromString(inputString, "&amp;uid={{userId}}", "&uid={{userId}}");
}

export const DEFAULT_TOUCHPOINT_DATA = { htmlOutput: "<>", jsonOutput: undefined };

const REVIEW_STATUSES = [
  TouchpointStatuses.DRAFT,
  TouchpointStatuses.SYSTEM_DRAFT,
  TouchpointStatuses.CLIENT_REVIEW,
];

const CLIENT_PORTAL_VERSION_SELECT_VERSION_STATUS_LABELS: ReadonlySet<string> = new Set([
  TouchpointStatuses.PUBLISHED,
  TouchpointStatuses.ARCHIVED,
  TouchpointStatuses.REJECTED,
]);

const CLIENT_COMMENTABLE_STATUSES: ReadonlySet<string> = new Set([
  TouchpointStatuses.CLIENT_REVIEW,
]);

export const getIsCommentingDisabled = (
  isClientUser: boolean,
  status: string | undefined,
): boolean => {
  if (!isClientUser) {
    return false;
  }

  return !status || !CLIENT_COMMENTABLE_STATUSES.has(status);
};

/** Client version-select label: internal names for published/archived/rejected; client review names elsewhere. */
export const getClientPortalVersionSelectLabel = (
  statuses: TouchpointStatusDictionaryItem | undefined,
  status: string | undefined,
): string | undefined => {
  if (!statuses) {
    return undefined;
  }

  if (status && CLIENT_PORTAL_VERSION_SELECT_VERSION_STATUS_LABELS.has(status)) {
    return statuses.versionStatusName;
  }

  return statuses.clientReviewStatusName || statuses.versionStatusName;
};

/**
 * Finds the detailed status information (names for admin/client) from the dictionary.
 */
export const findStatusDetails = (
  status: string | undefined,
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[],
) => {
  if (!status) return undefined;
  return touchpointVersionStatuses.find((item) => item.status === status);
};

/**
 * Checks if a touchpoint version is in a state that allows review/batching.
 */
export const isTouchpointInReview = (touchpoint: {
  latestVersion?: { status?: string | null } | null;
}): boolean => {
  const status = touchpoint?.latestVersion?.status;
  return !!(status && REVIEW_STATUSES.includes(status as any));
};
